import { ChangeDetectionStrategy, Component, DestroyRef, computed, effect, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, NavigationEnd, Router, RouterLink, RouterLinkActive, RouterOutlet } from '@angular/router';
import { finalize, filter, startWith } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { NotificationStoreService } from '@app/core/services/notification-store.service';
import { AuthStoreService } from '@app/core/services/auth-store.service';
import { AuthApiService } from '@app/core/services/auth-api.service';
import { AccountStateService } from '@app/core/services/account-state.service';
import { AccountStatusPollingService } from '@app/core/services/account-status-polling.service';
import { AccountContextService } from '@app/core/services/account-context.service';
import { AdminImpersonationService } from '@app/core/services/admin-impersonation.service';
import { PropertyContextService } from '@app/core/services/property-context.service';
import { PwaService } from '@app/core/services/pwa.service';
import { UserTourService } from '@app/core/services/user-tour.service';
import { StatusBadgeComponent } from '@app/shared/components/status-badge.component';
import {
  AccountAccessStateResponse,
  PropertySummary,
  StripePaymentSetupIntent,
  StripePaymentSetupResult,
  SubscriptionPlan,
  SubscriptionSettings,
  UserRole
} from '@app/core/models';
import { PartnerStateService } from '@app/features/partner/partner-state.service';
import { parseApiError } from '@app/shared/utils/api-error.utils';
import { OwnerStatusCardComponent } from '@app/features/owner/components/owner-status-card.component';
import { buildOwnerStatusWidget } from '@app/core/constants/owner-status-content';
import { SettingsApiService } from '@app/features/settings/settings-api.service';
import { StripeCardSetupComponent } from '@app/shared/components/stripe-card-setup.component';
import { WorkflowTourComponent } from '@app/features/help/components/workflow-tour.component';
import { AddonsStoreService } from '@app/features/addons/addons-store.service';

export interface AppShellNavItem {
  label: string;
  route: string;
  roles: UserRole[];
}

export const propertySelectorLabel = (property: PropertySummary, properties: readonly PropertySummary[] = []): string => {
  const location = [property.city, property.state].filter(Boolean).join(', ');
  const label = location ? `${property.name}_${location}` : property.name;
  const peers = properties.filter(x => x.name.trim().toLowerCase() === property.name.trim().toLowerCase());
  if (peers.length < 2) return label;
  const code = property.code?.trim();
  const identifier = code && peers.filter(x => x.code?.trim().toLowerCase() === code.toLowerCase()).length === 1 ? code : property.id;
  return `${label} · ${identifier}`;
};

export const APP_SHELL_NAV_ITEMS: readonly AppShellNavItem[] = [
  { label: 'Dashboard', route: '/dashboard', roles: ['owner', 'accountant'] },
  { label: 'Action Queue', route: '/action-queue', roles: ['owner', 'accountant'] },
  { label: 'Properties', route: '/properties', roles: ['owner'] },
  { label: 'Imports', route: '/imports', roles: ['owner'] },
  { label: 'Addons', route: '/addons', roles: ['owner'] },
  { label: 'Decision AI Engine', route: '/signals', roles: ['owner', 'accountant'] },
  { label: 'Certification', route: '/certifications', roles: ['owner', 'accountant'] },
  { label: 'Settings', route: '/settings', roles: ['owner', 'accountant'] },
  { label: 'Help & FAQ', route: '/help', roles: ['owner', 'accountant'] },
  { label: 'Dashboard', route: '/admin/dashboard', roles: ['admin'] },
  { label: 'Owners', route: '/admin/clients', roles: ['admin'] },
  { label: 'Webhook events', route: '/admin/webhook-events', roles: ['admin'] },
  { label: 'Partners', route: '/admin/partners', roles: ['admin'] },
  { label: 'Payments', route: '/admin/payments', roles: ['admin'] },
  { label: 'Repull Imports', route: '/admin/repull-imports', roles: ['admin'] },
  { label: 'Notifications', route: '/admin/notification-operations', roles: ['admin'] },
  { label: 'AI Messaging', route: '/admin/ai-guest-messaging', roles: ['admin'] },
  { label: 'Users', route: '/admin/users', roles: ['admin'] },
  { label: 'Settings', route: '/admin/settings', roles: ['admin'] },
  { label: 'Partner Dashboard', route: '/partner/dashboard', roles: ['partner'] },
  { label: 'Clients', route: '/partner/clients', roles: ['partner'] },
  { label: 'Commissions / Payouts', route: '/partner/commissions', roles: ['partner'] },
  { label: 'Profile', route: '/partner/profile', roles: ['partner'] },
  { label: 'Settings', route: '/partner/settings', roles: ['partner'] }
];

export const appShellNavigationForRole = (role: UserRole | null): AppShellNavItem[] =>
  APP_SHELL_NAV_ITEMS.filter((item) => !role || item.roles.includes(role));

@Component({
  selector: 'app-shell',
  standalone: true,
  imports: [
    CommonModule,
    RouterOutlet,
    RouterLink,
    RouterLinkActive,
    StatusBadgeComponent,
    OwnerStatusCardComponent,
    StripeCardSetupComponent,
    WorkflowTourComponent
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="min-h-screen bg-transparent">
      <div class="flex min-h-screen">
        <aside
          class="fixed inset-y-0 left-0 z-30 w-72 border-r border-slate-800 bg-slate-950 text-slate-100 transition-transform lg:translate-x-0"
          [class.-translate-x-full]="!menuOpen()"
        >
          <div class="flex h-full flex-col">
            <div class="border-b border-slate-800 px-6 py-6">
              <div class="flex items-center gap-3">
                <img
                  src="assets/branding/actionledger-check-seal.svg"
                  alt=""
                  aria-hidden="true"
                  class="h-10 w-10 rounded-xl shadow-lg shadow-cyan-950/30"
                />
                <p class="text-xs font-semibold uppercase tracking-[0.26em] text-slate-400">Action Ledgers</p>
              </div>
              <h1 class="mt-3 text-2xl font-semibold tracking-tight">Operations Console</h1>
              <p class="mt-2 text-sm text-slate-400">Material participation, market intelligence, and owner instruction workflows.</p>
            </div>

            <nav class="flex-1 space-y-1 overflow-y-auto px-4 py-5">
              @for (item of visibleNavItems(); track item.route) {
                <a
                  [routerLink]="item.route"
                  routerLinkActive="bg-slate-800 text-white shadow-panel-soft"
                  class="flex items-center rounded-2xl px-4 py-3 text-sm font-medium text-slate-300 transition hover:bg-slate-900 hover:text-white"
                  (click)="menuOpen.set(false)"
                >
                  {{ item.label }}
                </a>
              }
            </nav>

            <div class="border-t border-slate-800 px-6 py-5 text-sm text-slate-400">
              @if (authStore.role() === 'partner') {
                <div class="mb-4 flex items-center gap-3 rounded-2xl border border-slate-800 bg-slate-900/70 p-3">
                  @if (partnerAvatarUrl()) {
                    <img [src]="partnerAvatarUrl()!" alt="Partner avatar" class="h-12 w-12 rounded-full object-cover" />
                  } @else {
                    <div class="flex h-12 w-12 items-center justify-center rounded-full bg-slate-700 text-sm font-semibold text-white">
                      {{ partnerInitials() }}
                    </div>
                  }
                  <div class="min-w-0">
                    <p class="truncate font-medium text-slate-100">{{ partnerDisplayName() }}</p>
                    <p class="truncate text-slate-400">{{ partnerTitle() }}</p>
                  </div>
                </div>
              } @else {
                <p class="font-medium text-slate-200">{{ authStore.user()?.fullName }}</p>
                <p>{{ authStore.user()?.title }}</p>
              }
              <button
                type="button"
                class="mt-4 rounded-xl border border-slate-700 px-3 py-2 text-sm font-medium text-slate-200 hover:bg-slate-900"
                (click)="authStore.logout()"
              >
                Sign out
              </button>
            </div>
          </div>
        </aside>

        <div class="flex min-w-0 flex-1 flex-col lg:pl-72">
          @if (impersonation.session(); as session) {
            <div class="sticky top-0 z-30 border-b border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900 sm:px-6">
              <div class="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <p class="font-semibold">
                  Impersonating {{ session.actorType === 'client' ? 'Client' : 'Partner' }}:
                  {{ session.actorName || (session.actorType === 'client' ? 'Client Account' : 'Partner Account') }}
                </p>
                <button
                  type="button"
                  class="rounded-xl border border-amber-300 bg-white px-4 py-2 font-semibold text-amber-900 disabled:cursor-not-allowed disabled:opacity-60"
                  [disabled]="returningToAdmin()"
                  (click)="returnToAdmin()"
                >
                  {{ returningToAdmin() ? 'Returning...' : 'Return to Admin' }}
                </button>
              </div>

              @if (impersonationError()) {
                <p class="mt-3 text-sm text-rose-700">{{ impersonationError() }}</p>
              }
            </div>
          }

          @if (showOwnerOnboardingModal()) {
            <div class="fixed inset-0 z-[70] flex items-center justify-center bg-slate-950/50 p-4">
              <div class="w-full max-w-4xl overflow-hidden rounded-3xl bg-white shadow-2xl">
                <app-owner-status-card
                  [widget]="ownerBlockingWidget()"
                  [history]="statusPolling.history()"
                  [emailConfirmed]="accountState.emailConfirmed()"
                  [resendingConfirmation]="resendingConfirmation()"
                  (resendConfirmation)="resendOwnerConfirmation()"
                />
                @if (ownerBlockingWidget()?.status === 'PendingAdminSetup') {
                  <div class="border-t border-amber-200 bg-amber-50 px-5 py-4">
                    <button
                      type="button"
                      class="rounded-xl border border-amber-300 bg-white px-4 py-2 text-sm font-semibold text-amber-900 hover:bg-amber-100"
                      (click)="signOutFromOnboarding()"
                    >
                      Log out
                    </button>
                  </div>
                }
              </div>
            </div>
          }

          @if (showSubscriptionRequiredModal()) {
            <div class="fixed inset-0 z-[68] overflow-y-auto bg-slate-950/50 p-4">
              <div class="flex min-h-full items-center justify-center">
                <section class="max-h-[calc(100vh-2rem)] w-full max-w-5xl overflow-y-auto rounded-3xl border border-emerald-200 bg-white p-5 shadow-2xl sm:p-6" role="dialog" aria-modal="true" aria-labelledby="subscription-required-title">
                  <span class="rounded-full bg-emerald-100 px-3 py-1 text-xs font-semibold uppercase tracking-[0.18em] text-emerald-700">
                    Subscription required
                  </span>
                  <h2 id="subscription-required-title" class="mt-4 text-2xl font-semibold text-slate-950">Create your subscription</h2>
                  <p class="mt-3 max-w-2xl text-sm leading-6 text-slate-600">
                    Your account setup is complete. Enter payment information with Stripe to create your subscription and unlock the owner workspace.
                  </p>

                  <div class="mt-5 grid gap-5 lg:grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)]">
                    <div class="min-w-0 rounded-2xl border border-slate-200 bg-slate-50 p-4">
                      @if (subscriptionSettings()?.selectedPlan; as plan) {
                        <div class="flex flex-wrap items-start justify-between gap-3">
                          <div class="min-w-0 flex-1">
                            @if (showPlanTierDropdown()) {
                              <label>
                                <span class="mb-2 block text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">Plan</span>
                                <select
                                  class="field-input"
                                  [value]="selectedPlanTierId()"
                                  [disabled]="subscriptionSaving()"
                                  (change)="selectSubscriptionTier($any($event.target).value)"
                                >
                                  @for (planOption of planTierOptions(); track planOption.subscriptionPlanId) {
                                    <option [value]="planOption.subscriptionPlanId">{{ formatPlanTierLabel(planOption) }}</option>
                                  }
                                </select>
                              </label>
                            } @else {
                              <span class="mb-2 block text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">Plan</span>
                              <p class="text-base font-semibold text-slate-950">{{ formatPlanTierLabel(plan) }}</p>
                            }
                          </div>
                          <span class="rounded-full bg-white px-3 py-1 text-xs font-semibold text-slate-600">
                            {{ subscriptionSettings()?.propertyCount || 0 }} properties
                          </span>
                        </div>

                        <div class="mt-4">
                          <span class="mb-2 block text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">Billing</span>
                          <div class="grid gap-2 sm:grid-cols-2">
                            @for (planOption of sameTierSubscriptionPlans(); track planOption.subscriptionPlanId) {
                              <button
                                type="button"
                                class="rounded-2xl border px-3 py-3 text-left transition disabled:cursor-not-allowed disabled:opacity-60"
                                [class.border-emerald-500]="planOption.subscriptionPlanId === plan.subscriptionPlanId"
                                [class.bg-emerald-50]="planOption.subscriptionPlanId === plan.subscriptionPlanId"
                                [class.border-slate-200]="planOption.subscriptionPlanId !== plan.subscriptionPlanId"
                                [class.bg-white]="planOption.subscriptionPlanId !== plan.subscriptionPlanId"
                                [disabled]="subscriptionSaving()"
                                (click)="selectSubscriptionPlan(planOption.subscriptionPlanId)"
                              >
                                <span class="block text-sm font-semibold text-slate-950">{{ formatDurationLabel(planOption) }}</span>
                                <span class="mt-1 block text-sm text-slate-600">{{ formatRecurringFee(planOption) }}</span>
                                @if (planOption.durationMonths >= 12) {
                                  <span class="mt-2 inline-flex rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-semibold text-emerald-700">2 month free</span>
                                }
                              </button>
                            }
                          </div>
                        </div>

                        <p class="mt-3 text-sm text-slate-600">{{ plan.description }}</p>
                        <dl class="mt-4 grid gap-3 text-sm sm:grid-cols-2 lg:grid-cols-1 xl:grid-cols-2">
                          <div>
                            <dt class="font-semibold text-slate-500">Price</dt>
                            <dd class="mt-1 text-slate-900">{{ formatRecurringFee(plan) }}</dd>
                          </div>
                          <div>
                            <dt class="font-semibold text-slate-500">Setup fee</dt>
                            <dd class="mt-1 text-slate-900">
                              @if (plan.salesSetupFee !== null && plan.salesSetupFee !== undefined) {
                                @if (plan.setupFee !== null && plan.setupFee !== undefined) {
                                  <span class="mr-2 text-slate-400 line-through">{{ formatFee(plan.setupFee, plan.currency) }}</span>
                                }
                                <span class="font-semibold text-emerald-700">{{ formatFee(plan.salesSetupFee, plan.currency) }}</span>
                              } @else {
                                {{ formatFee(plan.setupFee, plan.currency) }}
                              }
                            </dd>
                          </div>
                        </dl>
                      } @else {
                        <div class="text-sm text-slate-600">Loading subscription plan...</div>
                      }
                    </div>

                    <div class="min-w-0 space-y-4">
                      @if (subscriptionError()) {
                        <div class="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
                          {{ subscriptionError() }}
                        </div>
                      }

                      @if (subscriptionLoading()) {
                        <div class="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-4 text-sm text-slate-600">
                          Preparing secure Stripe payment form...
                        </div>
                      } @else if (subscriptionSetup()?.clientSecret && subscriptionPublishableKey()) {
                        <app-stripe-card-setup
                          [clientSecret]="subscriptionSetup()?.clientSecret ?? ''"
                          [publishableKey]="subscriptionPublishableKey()"
                          [billingName]="authStore.user()?.fullName ?? ''"
                          [billingEmail]="authStore.user()?.email ?? ''"
                          title="Payment information"
                          submitLabel="Create Subscription"
                          submittingLabel="Creating Subscription..."
                          (submitted)="handleSubscriptionPaymentSetup($event)"
                          (cancelled)="resetSubscriptionPaymentForm()"
                        />
                      } @else {
                        <div class="flex flex-wrap gap-3">
                          <button
                            type="button"
                            class="rounded-xl bg-slate-900 px-4 py-2 text-sm font-semibold text-white"
                            (click)="prepareSubscriptionPaymentForm()"
                          >
                            Enter Payment Information
                          </button>
                        </div>
                      }
                    </div>
                  </div>
                </section>
              </div>
            </div>
          }

          @if (showNoPropertiesModal()) {
            <div class="fixed inset-0 z-[65] flex items-center justify-center bg-slate-950/50 p-4">
              <section class="w-full max-w-xl rounded-3xl border border-sky-200 bg-white p-6 shadow-2xl" role="dialog" aria-modal="true" aria-labelledby="no-properties-title">
                <span class="rounded-full bg-sky-100 px-3 py-1 text-xs font-semibold uppercase tracking-[0.18em] text-sky-700">
                  Property setup required
                </span>
                <h2 id="no-properties-title" class="mt-4 text-2xl font-semibold text-slate-950">Sync your first property</h2>
                <p class="mt-3 text-sm leading-6 text-slate-600">
                  Your account is active, but no properties are assigned yet. Sync your connected PMS, select the properties
                  to add, and review the subscription amount before approval.
                </p>
                <div class="mt-6 flex flex-wrap gap-3">
                  <button
                    type="button"
                    class="rounded-xl bg-slate-900 px-4 py-2 text-sm font-semibold text-white"
                    (click)="goToSyncProperties()"
                  >
                    Sync Properties
                  </button>
                </div>
              </section>
            </div>
          }

          <header class="sticky top-0 z-20 border-b border-white/70 bg-white/90 backdrop-blur">
            <div class="flex flex-wrap items-center gap-4 px-4 py-4 sm:px-6">
              <button
                type="button"
                class="rounded-xl border border-slate-200 p-2 text-slate-700 lg:hidden"
                (click)="toggleMenu()"
              >
                Menu
              </button>

              <div class="flex min-w-0 flex-1 flex-wrap items-end gap-3">
                @if (showAccountSwitcher()) {
                  <label class="block min-w-56 max-w-sm flex-1">
                    <span class="mb-2 block text-xs font-semibold uppercase tracking-[0.22em] text-slate-500">Owner account</span>
                    <select
                      class="field-input !text-xs"
                      [disabled]="accountContext.loading()"
                      [value]="accountContext.selectedAccountId() ?? ''"
                      (change)="selectAccount($any($event.target).value)"
                    >
                      @for (account of accountContext.accounts(); track account.clientAccountId) {
                        <option [value]="account.clientAccountId">{{ account.displayName }} · {{ account.membershipRole === 'PmsManager' ? 'PMS manager' : 'Owner' }}</option>
                      }
                    </select>
                  </label>
                }
                @if (showHeaderPropertySelector()) {
                  <label class="block min-w-56 max-w-md flex-1">
                    <span class="mb-2 block text-xs font-semibold uppercase tracking-[0.22em] text-slate-500">Properties</span>
                    <select
                      class="field-input !text-xs"
                      [disabled]="propertyContext.loading() || !propertyContext.properties().length"
                      [value]="propertyContext.selectedPropertyId() ?? ''"
                      (change)="propertyContext.selectProperty($any($event.target).value)"
                    >
                      @if (propertyContext.loading()) {
                        <option value="">Loading properties...</option>
                      } @else if (!propertyContext.properties().length) {
                        <option value="">No properties assigned</option>
                      } @else {
                        @for (property of propertyContext.properties(); track property.id) {
                          <option [value]="property.id">{{ propertySelectorLabel(property, propertyContext.properties()) }}</option>
                        }
                      }
                    </select>
                  </label>
                } @else {
                  <div class="min-w-0">
                    <p class="text-xs font-semibold uppercase tracking-[0.22em] text-slate-500">
                      {{ breadcrumbs().length ? breadcrumbs().join(' / ') : 'Dashboard' }}
                    </p>
                    <h2 class="truncate text-xl font-semibold text-slate-950">{{ currentTitle() }}</h2>
                  </div>
                }
              </div>

              @if (showManualActionShortcut()) {
                <a
                  routerLink="/manual-actions"
                  class="inline-flex h-10 w-10 items-center justify-center rounded-2xl border border-slate-200 bg-white text-slate-700 transition hover:border-sky-300 hover:bg-sky-50 hover:text-sky-800"
                  aria-label="Add manually"
                  title="Add manually"
                >
                  <span class="inline-flex h-8 w-8 items-center justify-center rounded-xl bg-sky-100 text-sky-700">
                    <svg class="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" aria-hidden="true">
                      <path d="M12 5v14M5 12h14" stroke-linecap="round" stroke-linejoin="round" />
                    </svg>
                  </span>
                </a>
              }

              @if (userTour.canUseTour()) {
                <button
                  type="button"
                  data-app-shell-tour-action
                  class="inline-flex h-10 w-10 items-center justify-center rounded-2xl border border-slate-200 bg-white text-slate-700 transition hover:border-sky-300 hover:bg-sky-50 hover:text-sky-800"
                  [attr.aria-label]="userTour.status() === 'inProgress' ? 'Resume owner workflow tour' : 'Open owner workflow tour'"
                  [title]="userTour.status() === 'inProgress' ? 'Resume tour' : 'Take the tour'"
                  (click)="userTour.takeTour()"
                >
                  <svg class="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" aria-hidden="true">
                    <circle cx="12" cy="12" r="9" />
                    <path d="m14.8 9.2-1.7 3.9-3.9 1.7 1.7-3.9 3.9-1.7Z" stroke-linecap="round" stroke-linejoin="round" />
                  </svg>
                </button>
              }

              <button
                type="button"
                class="relative inline-flex h-10 w-10 items-center justify-center rounded-xl border border-slate-200 bg-white text-slate-700"
                (click)="toggleNotifications()"
                aria-label="Notifications"
              >
                <svg class="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" aria-hidden="true">
                  <path d="M15 17h5l-1.4-1.4A2 2 0 0118 14.2V11a6 6 0 10-12 0v3.2a2 2 0 01-.6 1.4L4 17h5m6 0a3 3 0 11-6 0m6 0H9" stroke-linecap="round" stroke-linejoin="round" />
                </svg>
                @if (notificationStore.unreadCount()) {
                  <span class="absolute -right-1 -top-1 rounded-full bg-rose-500 px-1.5 py-0.5 text-[10px] font-semibold text-white">
                    {{ notificationStore.unreadCount() }}
                  </span>
                }
              </button>

              @if (!pwaService.online()) {
                <app-status-badge value="OFFLINE" />
              }

              @if (pwaService.canInstall()) {
                <button
                  type="button"
                  class="rounded-xl bg-slate-900 px-3 py-2 text-sm font-medium text-white"
                  (click)="installApp()"
                >
                  Install App
                </button>
              }

              @if (pwaService.updateAvailable()) {
                <button
                  type="button"
                  class="rounded-xl border border-emerald-300 bg-emerald-50 px-3 py-2 text-sm font-medium text-emerald-800"
                  (click)="applyUpdate()"
                >
                  Update Ready
                </button>
              }

              <button
                type="button"
                class="inline-flex h-10 w-10 items-center justify-center rounded-full border border-slate-200 bg-white text-sm font-semibold uppercase text-slate-700"
                (click)="toggleAccount()"
                [attr.aria-label]="authStore.user()?.fullName ?? 'Account menu'"
                [title]="authStore.user()?.fullName ?? ''"
              >
                {{ headerUserInitial() }}
              </button>
            </div>

            @if (notificationsOpen()) {
              <div class="border-t border-slate-200 bg-white px-4 py-4 sm:px-6">
                <div class="mb-3 flex items-center justify-between">
                  <p class="text-sm font-semibold text-slate-900">Notifications</p>
                  <button
                    type="button"
                    class="text-sm font-medium text-sky-700 disabled:cursor-not-allowed disabled:opacity-50"
                    [disabled]="notificationStore.markAllReadPending() || !notificationStore.unreadCount()"
                    (click)="markAllNotificationsRead($event)"
                  >
                    {{ notificationStore.markAllReadPending() ? 'Marking...' : 'Mark all read' }}
                  </button>
                </div>
                @if (notificationStore.error()) {
                  <div class="mb-3 rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
                    {{ notificationStore.error() }}
                  </div>
                }
                @if (!notificationStore.visibleNotifications().length) {
                  <div class="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-6 text-sm text-slate-600">
                    All notifications are read.
                  </div>
                } @else {
                  <div class="grid gap-3 lg:grid-cols-2">
                    @for (notification of notificationStore.visibleNotifications(); track notification.id) {
                    <article
                      class="rounded-2xl border p-4 transition-all duration-200 ease-out"
                      [class.border-slate-200]="notification.read"
                      [class.bg-slate-50]="notification.read"
                      [class.border-sky-200]="!notification.read"
                      [class.bg-sky-50]="!notification.read"
                      [class.opacity-0]="notificationStore.isNotificationDismissing(notification.id)"
                      [class.translate-y-[-6px]]="notificationStore.isNotificationDismissing(notification.id)"
                      [class.scale-[0.98]]="notificationStore.isNotificationDismissing(notification.id)"
                    >
                      <div class="flex items-start justify-between gap-3">
                        <div class="min-w-0">
                          <p class="text-sm font-semibold text-slate-900">{{ notification.title }}</p>
                          <p class="mt-1 text-sm text-slate-600">{{ notification.body }}</p>
                        </div>
                        @if (!notification.read) {
                          <button
                            type="button"
                            class="shrink-0 rounded-xl border border-slate-300 px-3 py-2 text-xs font-semibold text-slate-700 disabled:cursor-not-allowed disabled:opacity-50"
                            [disabled]="notificationStore.isNotificationPending(notification.id)"
                            (click)="markNotificationRead(notification.id, $event)"
                          >
                            {{ notificationStore.isNotificationPending(notification.id) ? 'Saving...' : 'Mark as read' }}
                          </button>
                        } @else {
                          <span class="shrink-0 rounded-full bg-emerald-100 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-emerald-700">
                            Read
                          </span>
                        }
                      </div>
                      <p class="mt-3 text-xs text-slate-500">{{ notification.createdAt | date: 'MMM d, y h:mm a' }}</p>
                    </article>
                    }
                  </div>
                }
              </div>
            }

            @if (accountOpen()) {
              <div class="border-t border-slate-200 bg-slate-50 px-4 py-4 sm:px-6">
                <div class="flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <p class="text-sm font-semibold text-slate-900">{{ authStore.user()?.fullName }}</p>
                    <p class="text-sm text-slate-600">{{ authStore.user()?.email }} - {{ authStore.user()?.role }}</p>
                  </div>
                  <button type="button" class="rounded-xl bg-slate-900 px-4 py-2 text-sm font-medium text-white" (click)="authStore.logout()">Sign out</button>
                </div>
              </div>
            }
          </header>

          @if (accountContext.accessRevoked()) {
            <div
              class="mx-4 mt-4 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900 sm:mx-6"
              role="status"
              aria-live="polite"
            >
              Access to your previous owner account changed. We selected an account you can still access.
            </div>
          }

          <main class="flex-1 px-4 py-6 sm:px-6">
            <router-outlet />
          </main>
        </div>
        @defer (when userTour.isOpen()) {
          <app-workflow-tour />
        }
      </div>
    </div>
  `
})
export class AppShellComponent {
  readonly authStore = inject(AuthStoreService);
  readonly accountState = inject(AccountStateService);
  readonly statusPolling = inject(AccountStatusPollingService);
  readonly impersonation = inject(AdminImpersonationService);
  readonly propertyContext = inject(PropertyContextService);
  readonly accountContext = inject(AccountContextService);
  readonly addonsStore = inject(AddonsStoreService);
  readonly propertySelectorLabel = propertySelectorLabel;
  readonly notificationStore = inject(NotificationStoreService);
  readonly pwaService = inject(PwaService);
  readonly partnerState = inject(PartnerStateService);
  readonly userTour = inject(UserTourService);

  private readonly authApi = inject(AuthApiService);
  private readonly settingsApi = inject(SettingsApiService);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly destroyRef = inject(DestroyRef);

  readonly menuOpen = signal(false);
  readonly notificationsOpen = signal(false);
  readonly accountOpen = signal(false);
  readonly returningToAdmin = signal(false);
  readonly impersonationError = signal('');
  readonly resendingConfirmation = signal(false);
  readonly propertySetupPromptDismissed = signal(false);
  readonly accountAccessState = signal<AccountAccessStateResponse | null>(null);
  readonly subscriptionSettings = signal<SubscriptionSettings | null>(null);
  readonly subscriptionSetup = signal<StripePaymentSetupIntent | null>(null);
  readonly subscriptionLoading = signal(false);
  readonly subscriptionSaving = signal(false);
  readonly subscriptionError = signal('');
  readonly currentTitle = signal('Operations Dashboard');
  readonly breadcrumbs = signal<string[]>([]);
  readonly currentUrl = signal(this.router.url);
  private readonly notificationsLoadedForUserId = signal<string | null>(null);
  private readonly partnerProfileLoadedForUserId = signal<string | null>(null);
  private readonly accountAccessLoadedForUserId = signal<string | null>(null);

  readonly subscriptionRecoveryRequired = computed(() => {
    const subscription = this.accountAccessState()?.subscription;
    const status = subscription?.status?.trim().toLowerCase();
    return this.accountState.blockedReasonCode() === 'SubscriptionRequired' ||
      status === 'canceled' ||
      status === 'cancelled' ||
      status === 'expired';
  });
  readonly visibleNavItems = computed(() => {
    const role = this.authStore.role();
    if (role === 'owner' && this.subscriptionRecoveryRequired()) {
      return [
        { label: 'Certification downloads', route: '/certifications?readOnly=1', roles: ['owner'] as UserRole[] },
        { label: 'Restore subscription', route: '/subscription/reactivate', roles: ['owner'] as UserRole[] }
      ];
    }

    return appShellNavigationForRole(role).filter((item) =>
      (item.route !== '/imports' || this.accountContext.capabilities()?.canViewImportOperations === true) &&
      (item.route !== '/signals' || role !== 'owner' || this.addonsStore.decisionEngineEnabled())
    );
  });
  readonly showHeaderPropertySelector = computed(() => {
    const role = this.authStore.role();
    return (role === 'owner' && this.accountState.isLive()) ||
      role === 'accountant';
  });
  readonly showAccountSwitcher = computed(() =>
    this.authStore.role() !== 'partner' && this.accountContext.accounts().length > 1);
  readonly ownerBlockingWidget = computed(() => {
    const state = this.accountState.state();
    if (!state) {
      return null;
    }

    return buildOwnerStatusWidget(
      state.onboardingStatus ?? 'PendingAdminSetup',
      this.accountState.ownerStatusWidget()
    );
  });
  readonly showOwnerOnboardingModal = computed(() => {
    const state = this.accountState.state();
    return this.authStore.role() === 'owner' &&
      !!state &&
      state.isLimitedAccess === true &&
      !this.accountState.isLive();
  });
  readonly showNoPropertiesModal = computed(() =>
    this.authStore.role() === 'owner' &&
    this.accountState.isLive() &&
    !this.showSubscriptionRequiredModal() &&
    !this.propertySetupPromptDismissed() &&
    !this.propertyContext.loading() &&
    this.propertyContext.properties().length === 0 &&
    !this.router.url.startsWith('/settings') &&
    !this.router.url.startsWith('/properties')
  );
  readonly showSubscriptionRequiredModal = computed(() => {
    const subscription = this.accountAccessState()?.subscription;
    return this.authStore.role() === 'owner' &&
      this.accountState.isLive() &&
      !this.subscriptionRecoveryRequired() &&
      !this.router.url.startsWith('/settings') &&
      !!subscription &&
      (!subscription.isValid || subscription.requiresPaymentAction);
  });
  readonly subscriptionPublishableKey = computed(() =>
    this.subscriptionSetup()?.publishableKey ?? ''
  );
  readonly availableSubscriptionPlans = computed(() => this.subscriptionSettings()?.availablePlans ?? []);
  readonly sameTierSubscriptionPlans = computed(() => {
    const settings = this.subscriptionSettings();
    const selectedPlan = settings?.selectedPlan;
    if (!settings || !selectedPlan) {
      return [];
    }

    return settings.availablePlans
      .filter((plan) => this.isSamePlanTier(plan, selectedPlan))
      .sort((first, second) => first.durationMonths - second.durationMonths);
  });
  readonly planTierOptions = computed(() => {
    const plans = this.availableSubscriptionPlans();
    const seen = new Set<string>();
    const options: SubscriptionPlan[] = [];

    for (const plan of plans) {
      const key = this.planTierKey(plan);
      if (seen.has(key)) {
        continue;
      }

      seen.add(key);
      options.push(plan);
    }

    return options.sort((first, second) =>
      first.minProperties - second.minProperties ||
      (first.maxProperties ?? Number.MAX_SAFE_INTEGER) - (second.maxProperties ?? Number.MAX_SAFE_INTEGER)
    );
  });
  readonly selectedPlanTierId = computed(() => {
    const selectedPlan = this.subscriptionSettings()?.selectedPlan;
    if (!selectedPlan) {
      return '';
    }

    return this.planTierOptions().find((plan) => this.isSamePlanTier(plan, selectedPlan))?.subscriptionPlanId
      ?? selectedPlan.subscriptionPlanId;
  });
  readonly showPlanTierDropdown = computed(() => {
    const settings = this.subscriptionSettings();
    const selectedPlan = settings?.selectedPlan;
    if (!settings || !selectedPlan) {
      return true;
    }

    return settings.propertyCount <= 0 ||
      (selectedPlan.maxProperties !== null &&
        selectedPlan.maxProperties !== undefined &&
        settings.propertyCount < selectedPlan.maxProperties);
  });
  readonly showManualActionShortcut = computed(() => {
    const role = this.authStore.role();
    if (role === 'accountant') {
      return true;
    }

    return role === 'owner' && this.accountState.isLive() && this.propertyContext.properties().length > 0;
  });

  readonly partnerDisplayName = computed(
    () => this.partnerState.profile()?.displayName || this.authStore.user()?.fullName || 'Partner'
  );
  readonly partnerTitle = computed(() => this.authStore.user()?.title || 'Operating Partner');
  readonly partnerAvatarUrl = computed(() => this.partnerState.profile()?.avatarUrl ?? null);

  selectAccount(clientAccountId: string): void {
    this.accountContext.selectAccount(clientAccountId);
  }

  constructor() {
    this.statusPolling.start();

    effect(() => {
      const userId = this.authStore.user()?.id ?? null;

      if (!userId) {
        this.notificationsLoadedForUserId.set(null);
        return;
      }

      if (this.notificationsLoadedForUserId() === userId) {
        return;
      }

      this.notificationStore.load().pipe(takeUntilDestroyed(this.destroyRef)).subscribe();
      this.notificationsLoadedForUserId.set(userId);
    }, { allowSignalWrites: true });

    effect(() => {
      const user = this.authStore.user();

      if (!user || user.role !== 'partner') {
        this.partnerProfileLoadedForUserId.set(null);
        return;
      }

      if (this.partnerProfileLoadedForUserId() === user.id) {
        return;
      }

      this.partnerState.loadProfile().pipe(takeUntilDestroyed(this.destroyRef)).subscribe();
      this.partnerProfileLoadedForUserId.set(user.id);
    }, { allowSignalWrites: true });

    effect(() => {
      const user = this.authStore.user();

      if (!user || user.role !== 'owner' || !this.accountState.isLive()) {
        this.accountAccessState.set(null);
        this.accountAccessLoadedForUserId.set(null);
        return;
      }

      if (this.accountAccessLoadedForUserId() === user.id) {
        return;
      }

      this.accountAccessLoadedForUserId.set(user.id);
      this.authApi
        .getAccountAccessState()
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: (state) => this.accountAccessState.set(state),
          error: () => {
            this.accountAccessState.set(null);
            this.accountAccessLoadedForUserId.set(null);
          }
        });
    }, { allowSignalWrites: true });

    effect(() => {
      if (!this.showSubscriptionRequiredModal()) {
        this.subscriptionSettings.set(null);
        this.subscriptionSetup.set(null);
        this.subscriptionLoading.set(false);
        this.subscriptionSaving.set(false);
        this.subscriptionError.set('');
        return;
      }

      if (this.subscriptionSettings() || this.subscriptionLoading()) {
        return;
      }

      this.prepareSubscriptionPaymentForm();
    }, { allowSignalWrites: true });

    effect(() => {
      if (!this.subscriptionRecoveryRequired()) {
        return;
      }

      const currentPath = this.currentUrl().split('?')[0].split('#')[0];
      if (currentPath.startsWith('/certifications') || currentPath === '/subscription/reactivate') {
        return;
      }

      void this.router.navigate(['/subscription/reactivate']);
    });

    effect(() => {
      const routePath = this.currentUrl().split('?')[0].split('#')[0];
      const readyForTour =
        routePath === '/dashboard' &&
        this.accountState.isLive() &&
        !this.propertyContext.loading() &&
        !this.showOwnerOnboardingModal() &&
        !this.showNoPropertiesModal() &&
        !this.showSubscriptionRequiredModal();

      if (readyForTour) {
        this.userTour.autoOfferForLiveDashboard();
      }
    }, { allowSignalWrites: true });

    this.router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        startWith(new NavigationEnd(0, this.router.url, this.router.url)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((event) => {
        this.impersonationError.set('');
        this.currentUrl.set(event.urlAfterRedirects);
        if (!this.router.url.startsWith('/properties')) {
          this.propertySetupPromptDismissed.set(false);
        }
        this.syncRouteMeta();
      });
  }

  private syncRouteMeta(): void {
    let cursor: ActivatedRoute | null = this.route.firstChild;
    const crumbs: string[] = [];
    let title = 'Operations Dashboard';

    while (cursor) {
      const data = cursor.snapshot?.data ?? {};

      if (data['breadcrumb']) {
        crumbs.push(String(data['breadcrumb']));
      }
      if (data['title']) {
        title = String(data['title']);
      }
      cursor = cursor.firstChild ?? null;
    }

    this.currentTitle.set(title);
    this.breadcrumbs.set(crumbs);
  }

  toggleMenu(): void {
    this.menuOpen.set(!this.menuOpen());
  }

  toggleNotifications(): void {
    this.notificationsOpen.set(!this.notificationsOpen());
  }

  markAllNotificationsRead(event?: Event): void {
    event?.stopPropagation();
    this.notificationStore.markAllRead();
  }

  markNotificationRead(notificationId: string, event?: Event): void {
    event?.stopPropagation();
    this.notificationStore.markRead(notificationId);
  }

  toggleAccount(): void {
    this.accountOpen.set(!this.accountOpen());
  }

  signOutFromOnboarding(): void {
    this.authStore.logout();
  }

  goToSyncProperties(): void {
    this.propertySetupPromptDismissed.set(true);
    void this.router.navigate(['/properties'], {
      queryParams: {
        syncProperties: 'true'
      }
    });
  }

  prepareSubscriptionPaymentForm(): void {
    if (this.subscriptionLoading()) {
      return;
    }

    this.subscriptionLoading.set(true);
    this.subscriptionError.set('');
    this.settingsApi
      .getSubscriptionPreview()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (subscription) => {
          this.subscriptionSettings.set(subscription);
          this.settingsApi
            .createPaymentMethodSetupIntent()
            .pipe(
              finalize(() => this.subscriptionLoading.set(false)),
              takeUntilDestroyed(this.destroyRef)
            )
            .subscribe({
              next: (setup) => this.subscriptionSetup.set(setup),
              error: (error) => {
                this.subscriptionSetup.set(null);
                this.subscriptionError.set(
                  parseApiError(error, 'Unable to prepare Stripe payment entry right now.').message
                );
              }
            });
        },
        error: (error) => {
          this.subscriptionLoading.set(false);
          this.subscriptionSettings.set(null);
          this.subscriptionSetup.set(null);
          this.subscriptionError.set(
            parseApiError(error, 'Unable to load subscription plan for this account.').message
          );
        }
      });
  }

  handleSubscriptionPaymentSetup(result: StripePaymentSetupResult): void {
    if (this.subscriptionSaving()) {
      return;
    }

    this.subscriptionSaving.set(true);
    this.subscriptionError.set('');
    const subscriptionPlanId = this.subscriptionSettings()?.subscriptionPlanId
      ?? this.subscriptionSettings()?.selectedPlan?.subscriptionPlanId
      ?? null;
    this.settingsApi
      .attachPaymentMethod({ ...result, subscriptionPlanId })
      .pipe(
        finalize(() => this.subscriptionSaving.set(false)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: (subscription) => {
          this.subscriptionSettings.set(subscription);
          this.subscriptionSetup.set(null);
          this.refreshAccountAccessState();
        },
        error: (error) => {
          this.subscriptionError.set(
            parseApiError(error, 'Unable to create subscription with that payment method.').message
          );
        }
      });
  }

  selectSubscriptionPlan(subscriptionPlanId: string): void {
    const settings = this.subscriptionSettings();
    if (!settings) {
      return;
    }

    const selectedPlan = settings.availablePlans.find((plan) => plan.subscriptionPlanId === subscriptionPlanId);
    if (!selectedPlan) {
      return;
    }

    this.subscriptionSettings.set({
      ...settings,
      subscriptionPlanId: selectedPlan.subscriptionPlanId,
      planCode: selectedPlan.code,
      planName: selectedPlan.displayName,
      priceId: selectedPlan.stripePriceId ?? null,
      productId: selectedPlan.stripeProductId ?? null,
      durationMonths: selectedPlan.durationMonths,
      price: selectedPlan.price ?? null,
      setupFee: selectedPlan.setupFee ?? null,
      salesSetupFee: selectedPlan.salesSetupFee ?? null,
      selectedPlan
    });
    this.subscriptionError.set('');
  }

  selectSubscriptionTier(subscriptionPlanId: string): void {
    const settings = this.subscriptionSettings();
    if (!settings) {
      return;
    }

    const tierPlan = settings.availablePlans.find((plan) => plan.subscriptionPlanId === subscriptionPlanId);
    const currentDuration = settings.selectedPlan?.durationMonths ?? settings.durationMonths ?? 1;
    const selectedPlan = tierPlan
      ? settings.availablePlans.find((plan) =>
          this.isSamePlanTier(plan, tierPlan) && plan.durationMonths === currentDuration
        ) ?? tierPlan
      : null;

    if (!selectedPlan) {
      return;
    }

    this.selectSubscriptionPlan(selectedPlan.subscriptionPlanId);
  }

  resetSubscriptionPaymentForm(): void {
    this.subscriptionSetup.set(null);
    this.subscriptionError.set('');
  }

  refreshAccountAccessState(): void {
    this.authApi
      .getAccountAccessState()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (state) => this.accountAccessState.set(state),
        error: () => this.accountAccessState.set(null)
      });
  }

  formatFee(value: number | null | undefined, currency: string | null | undefined): string {
    if (value === null || value === undefined) {
      return 'Not set';
    }

    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: (currency || 'usd').toUpperCase()
    }).format(value);
  }

  formatRecurringFee(plan: SubscriptionPlan | null | undefined): string {
    if (!plan || plan.price === null || plan.price === undefined) {
      return 'Not set';
    }

    return `${this.formatFee(plan.price, plan.currency)} per ${this.durationUnit(plan)}`;
  }

  formatDurationLabel(plan: SubscriptionPlan): string {
    return plan.durationMonths >= 12 ? 'Yearly' : 'Monthly';
  }

  formatPlanTierLabel(plan: SubscriptionPlan): string {
    if (plan.maxProperties === null || plan.maxProperties === undefined) {
      return `${plan.minProperties}+ Properties`;
    }

    return `${plan.minProperties}-${plan.maxProperties} Properties`;
  }

  private durationUnit(plan: SubscriptionPlan): string {
    return plan.durationMonths >= 12 ? 'year' : 'month';
  }

  private isSamePlanTier(first: SubscriptionPlan, second: SubscriptionPlan): boolean {
    return first.minProperties === second.minProperties &&
      (first.maxProperties ?? null) === (second.maxProperties ?? null);
  }

  private planTierKey(plan: SubscriptionPlan): string {
    return `${plan.minProperties}:${plan.maxProperties ?? 'plus'}`;
  }

  returnToAdmin(): void {
    if (!this.impersonation.isImpersonating() || this.returningToAdmin()) {
      return;
    }

    this.returningToAdmin.set(true);
    this.impersonationError.set('');
    this.impersonation.stopImpersonation().pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: () => {
        this.returningToAdmin.set(false);
      },
      error: (error) => {
        this.returningToAdmin.set(false);
        this.impersonationError.set(
          parseApiError(error, 'Unable to restore the admin session right now.').message
        );
      }
    });
  }

  resendOwnerConfirmation(): void {
    const email = this.authStore.user()?.email || this.accountState.state()?.email;
    if (!email || this.resendingConfirmation()) {
      return;
    }

    this.resendingConfirmation.set(true);
    this.authApi
      .resendEmailConfirmation({ email })
      .pipe(
        finalize(() => this.resendingConfirmation.set(false)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }

  installApp(): void {
    void this.pwaService.install();
  }

  applyUpdate(): void {
    void this.pwaService.applyUpdate();
  }

  partnerInitials(): string {
    return this.partnerDisplayName()
      .split(' ')
      .slice(0, 2)
      .map((part) => part[0]?.toUpperCase() ?? '')
      .join('');
  }

  headerUserInitial(): string {
    const nameParts =
      this.authStore.user()?.fullName
        ?.trim()
        .split(/\s+/)
        .filter(Boolean) ?? [];

    if (!nameParts.length) {
      return 'U';
    }

    if (nameParts.length === 1) {
      return nameParts[0].charAt(0).toUpperCase();
    }

    return `${nameParts[0].charAt(0)}${nameParts[nameParts.length - 1].charAt(0)}`.toUpperCase();
  }
}

